import { pb } from './pb'
import { parsePocketBaseInstant } from './session'

/**
 * The shared prayer board (`prayer_requests` + `prayer_amens`).
 *
 * Only the *shared* board comes to the web. The app also keeps private local prayers, and the
 * screen there promises in as many words that those never leave the phone. They are not synced,
 * not readable here, and there is nothing on this page that could show them. See PORTAL.md
 * section 1.
 *
 * Posting and amening stay free. PAYWALL.md lists write access to the board as the one plausible
 * community gate and then declines to take it, on the grounds that gating entry to the
 * sisterhood is not the thing to sell.
 */

export interface PrayerRequest {
  id: string
  author: string
  body: string
  is_answered: boolean
  created: string
  expand?: { author?: { id: string; name: string } }
}

export interface PrayerAmen {
  id: string
  request: string
  user: string
}

export interface BoardEntry extends PrayerRequest {
  amenCount: number
  /** The signed-in user's amen record id, so it can be taken back. Null when she has not. */
  myAmenId: string | null
}

export function authorName(request: PrayerRequest): string {
  return request.expand?.author?.name?.trim() || 'Sister'
}

/**
 * Requests newest first, plus every amen, grouped here.
 *
 * Two unfiltered fetches rather than a per-request count, exactly as the app does it: the board
 * is small, and the alternative is a filter for every row on screen.
 */
export async function loadBoard(myUserId: string): Promise<BoardEntry[]> {
  const [requests, amens] = await Promise.all([
    pb.collection('prayer_requests').getList<PrayerRequest>(1, 200, {
      sort: '-created',
      expand: 'author',
      // See recipes.ts: the SDK cancels a duplicate in-flight request, and a remount must not
      // cancel the board out from under the page.
      requestKey: null,
    }),
    pb.collection('prayer_amens').getList<PrayerAmen>(1, 500, { requestKey: null }),
  ])

  const byRequest = new Map<string, PrayerAmen[]>()
  for (const amen of amens.items) {
    const list = byRequest.get(amen.request)
    if (list) list.push(amen)
    else byRequest.set(amen.request, [amen])
  }

  return requests.items.map((request) => {
    const mine = byRequest.get(request.id) ?? []
    return {
      ...request,
      amenCount: mine.length,
      myAmenId: mine.find((amen) => amen.user === myUserId)?.id ?? null,
    }
  })
}

export async function postRequest(myUserId: string, body: string): Promise<void> {
  await pb.collection('prayer_requests').create({
    author: myUserId,
    body,
    is_answered: false,
  })
}

export async function setAnswered(requestId: string, answered: boolean): Promise<void> {
  await pb.collection('prayer_requests').update(requestId, { is_answered: answered })
}

export async function deleteRequest(requestId: string): Promise<void> {
  await pb.collection('prayer_requests').delete(requestId)
}

/** Adds an amen when [myAmenId] is null, takes it back otherwise. */
export async function toggleAmen(
  requestId: string,
  myUserId: string,
  myAmenId: string | null,
): Promise<void> {
  if (myAmenId) {
    await pb.collection('prayer_amens').delete(myAmenId)
  } else {
    await pb.collection('prayer_amens').create({ request: requestId, user: myUserId })
  }
}

/** "just now" / "5m ago" / "3h ago" / "Jul 12", the same ladder the app uses. */
export function relativeTime(created: string): string {
  const instant = parsePocketBaseInstant(created)
  if (!instant) return ''
  const minutes = Math.floor((Date.now() - instant.getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}h ago`
  return instant.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
